import type { SimulationEdge, SimulationNode } from '../models/simulation';

export const getInboundNodeIds = (nodeId: string, edges: SimulationEdge[]): string[] =>
    edges.filter((edge) => edge.target === nodeId).map((edge) => edge.source);

export const getOutboundNodeIds = (nodeId: string, edges: SimulationEdge[]): string[] =>
    edges.filter((edge) => edge.source === nodeId).map((edge) => edge.target);

export const getAllAncestors = (nodeId: string, edges: SimulationEdge[]): Set<string> => {
    const ancestors = new Set<string>();
    const visited = new Set<string>();
    const inbound = new Map<string, string[]>();

    edges.forEach((edge) => {
        const parents = inbound.get(edge.target) ?? [];
        parents.push(edge.source);
        inbound.set(edge.target, parents);
    });

    const traverse = (currentId: string) => {
        if (visited.has(currentId)) return;
        visited.add(currentId);

        const parents = inbound.get(currentId) ?? [];
        parents.forEach((parentId) => {
            ancestors.add(parentId);
            traverse(parentId);
        });
    };

    traverse(nodeId);
    return ancestors;
};

export const getAllDescendants = (nodeId: string, edges: SimulationEdge[]): Set<string> => {
    const descendants = new Set<string>();
    const visited = new Set<string>();
    const outbound = new Map<string, string[]>();

    edges.forEach((edge) => {
        const children = outbound.get(edge.source) ?? [];
        children.push(edge.target);
        outbound.set(edge.source, children);
    });

    const traverse = (currentId: string) => {
        if (visited.has(currentId)) return;
        visited.add(currentId);

        const children = outbound.get(currentId) ?? [];
        children.forEach((childId) => {
            descendants.add(childId);
            traverse(childId);
        });
    };

    traverse(nodeId);
    return descendants;
};

export const getImmediateAncestors = (nodeId: string, edges: SimulationEdge[]): Set<string> => {
    const ancestors = new Set<string>();
    const parents = getInboundNodeIds(nodeId, edges);
    parents.forEach((parentId) => {
        ancestors.add(parentId);
    });
    return ancestors;
};

export const getImmediateDescendants = (nodeId: string, edges: SimulationEdge[]): Set<string> => {
    const descendants = new Set<string>();
    const children = getOutboundNodeIds(nodeId, edges);
    children.forEach((childId) => {
        descendants.add(childId);
    });
    return descendants;
};

export const topologicalSort = (
    nodes: SimulationNode[],
    edges: SimulationEdge[]
): SimulationNode[] => {
    const indegree = new Map<string, number>();
    const adjacency = new Map<string, string[]>();
    const nodeById = new Map<string, SimulationNode>();

    nodes.forEach((node) => {
        indegree.set(node.id, 0);
        adjacency.set(node.id, []);
        nodeById.set(node.id, node);
    });

    edges.forEach((edge) => {
        indegree.set(edge.target, (indegree.get(edge.target) ?? 0) + 1);
        adjacency.get(edge.source)?.push(edge.target);
    });

    const queue: SimulationNode[] = nodes.filter((node) => (indegree.get(node.id) ?? 0) === 0);
    const sorted: SimulationNode[] = [];

    while (queue.length > 0) {
        const node = queue.shift()!;
        sorted.push(node);
        const neighbors = adjacency.get(node.id) ?? [];
        neighbors.forEach((neighborId) => {
            const count = (indegree.get(neighborId) ?? 0) - 1;
            indegree.set(neighborId, count);
            if (count === 0) {
                const neighborNode = nodeById.get(neighborId);
                if (neighborNode) {
                    queue.push(neighborNode);
                }
            }
        });
    }

    if (sorted.length !== nodes.length) {
        throw new Error('Graph contains cycles; unable to evaluate the workflow.');
    }

    return sorted;
};
